// src/lib/db.js
// "People" in the CRM = Firestore docs (default collection `users`). That is separate from
// Firebase Authentication: Auth does not auto-create Firestore rows. Most apps sync signup → `users/{uid}`.
// Override: REACT_APP_CRM_CONTACTS_COLLECTION / REACT_APP_CRM_COMPANIES_COLLECTION
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  setDoc, query, where, orderBy, limit, onSnapshot, writeBatch,
  serverTimestamp, Timestamp, arrayUnion, arrayRemove, increment
} from "firebase/firestore";
import { db } from "./firebase";
import { AUTO_TAG_RULES } from "./constants";
import { deriveContactHealthScore, deriveContactLeadScore } from "./healthScore";
import { resolveContactDisplayName } from "./contactDisplayName";
import { enrichContactProfileFields } from "./contactProfileFields";
import { enrichDeviceProfileFields } from "./deviceProfile";

const CONTACTS = process.env.REACT_APP_CRM_CONTACTS_COLLECTION || "users";
const COMPANIES = process.env.REACT_APP_CRM_COMPANIES_COLLECTION || "companies";

/** Firestore collection ids (for UI hints). `people` = CRM “contacts” / authenticated profiles. */
export const CRM_COLLECTIONS = { people: CONTACTS, companies: COMPANIES };

function getByPath(obj, path) {
  if (!obj || typeof path !== "string") return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    cur = cur?.[p];
  }
  return cur;
}

/** Accept Firestore Timestamp, Date, millis number, ISO string, or { seconds, nanoseconds? }. */
function coerceTimestampMs(v) {
  if (v == null) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "object" && typeof v.seconds === "number") return v.seconds * 1000;
  if (typeof v === "number" && v > 1e11) return v;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function normalizeTimestampValue(v) {
  if (v == null) return undefined;
  if (typeof v.toDate === "function") return v;
  if (typeof v.toMillis === "function") return v;
  if (v instanceof Date) return Timestamp.fromDate(v);
  if (typeof v === "object" && typeof v.seconds === "number")
    return new Timestamp(v.seconds, v.nanoseconds ?? 0);
  if (typeof v === "number" && v > 1e11) return Timestamp.fromMillis(v);
  if (typeof v === "string") {
    const ms = Date.parse(v);
    if (!Number.isNaN(ms)) return Timestamp.fromMillis(ms);
  }
  return undefined;
}

const CONTACT_CREATED_AT_FIELDS = [
  "createdAt",
  "created_at",
  "signupAt",
  "accountCreatedAt",
  "registeredAt",
  "joinDate",
  "signedUpAt",
  "userCreatedAt",
  "registrationDate",
  "authCreatedAt",
  "created_time",
  "metadata.creationTime",
  "metadata.createdAt",
  "auth.createdAt",
];

const CONTACT_UPDATED_AT_FIELDS = [
  "updatedAt",
  "updated_at",
  "modifiedAt",
  "lastModifiedAt",
  "lastUpdatedAt",
  "metadata.lastSignInTime",
];

function firstNormalizedTimestamp(data, paths) {
  for (const p of paths) {
    const raw = p.includes(".") ? getByPath(data, p) : data?.[p];
    const n = normalizeTimestampValue(raw);
    if (n != null) return n;
  }
  return undefined;
}

/** Map Firestore user/profile docs (e.g. from Auth sync) into CRM row shape. */
export function normalizeContactForCRM(row) {
  if (!row) return null;
  const { id, ...data } = row;
  const name = resolveContactDisplayName(data);
  const email = data.email || "";
  const customFields =
    data.customFields && typeof data.customFields === "object" && !Array.isArray(data.customFields)
      ? data.customFields
      : {};
  const createdAtResolved =
    firstNormalizedTimestamp(data, CONTACT_CREATED_AT_FIELDS) || normalizeTimestampValue(data.createdAt);
  const updatedAtResolved =
    firstNormalizedTimestamp(data, CONTACT_UPDATED_AT_FIELDS) || normalizeTimestampValue(data.updatedAt);
  const profileFields = enrichContactProfileFields(data);
  const deviceFields = enrichDeviceProfileFields(data);
  return {
    ...data,
    id,
    name,
    email,
    ...profileFields,
    ...deviceFields,
    isInternal: data.isInternal === true,
    lifetimeRevenue: (() => {
      const raw = data.lifetimeRevenue ?? data.total_invoiced ?? data.totalPaid;
      if (raw == null) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    })(),
    stage: data.stage || "lead",
    plan: data.plan || "Free",
    mrr: data.mrr ?? 0,
    totalScans: Number(data.totalScans ?? data.total_scans ?? profileFields.totalScansFromProfile ?? 0) || 0,
    healthScore: deriveContactHealthScore(data),
    healthScoreManual: data.healthScoreManual === true,
    leadScore: deriveContactLeadScore(data),
    leadScoreManual: data.leadScoreManual === true,
    tags: Array.isArray(data.tags) ? data.tags : [],
    firebaseUid: data.firebaseUid || data.uid || id,
    role: data.role || data.jobTitle || "",
    industry: data.industry || "",
    customFields,
    lastActiveAt: data.lastActiveAt || data.lastLoginAt || data.lastSeenAt || data.lastSignInTime,
    createdAt: createdAtResolved ?? data.createdAt,
    updatedAt: updatedAtResolved ?? data.updatedAt,
  };
}

function timestampFieldMs(obj, field) {
  return coerceTimestampMs(obj?.[field]);
}

/** Deal / generic doc created time (internal). */
function createdAtMs(data) {
  return timestampFieldMs(data, "createdAt");
}

/** Primary created time for sorting (falls back to updatedAt when createdAt missing). */
export function contactCreatedAtMs(contact) {
  return timestampFieldMs(contact, "createdAt");
}

export function contactUpdatedAtMs(contact) {
  return timestampFieldMs(contact, "updatedAt");
}

export function contactEffectiveCreatedMs(contact) {
  const c = contactCreatedAtMs(contact);
  return c > 0 ? c : contactUpdatedAtMs(contact);
}

export function contactLastActiveMs(contact) {
  return (
    timestampFieldMs(contact, "lastActiveAt") ||
    timestampFieldMs(contact, "lastLoginAt") ||
    timestampFieldMs(contact, "lastSeenAt") ||
    timestampFieldMs(contact, "lastSignInTime")
  );
}

/** Resolve a product user ref (uid, contact doc id, or email) to a CRM contact row. */
export function resolveContactByUserRef(contacts, ref) {
  if (ref == null || !Array.isArray(contacts)) return null;
  const s = String(ref).trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  return (
    contacts.find((c) => c.id === s) ||
    contacts.find((c) => String(c.firebaseUid || c.uid || "") === s) ||
    contacts.find((c) => (c.email || "").toLowerCase() === lower) ||
    null
  );
}

/**
 * @param {"created_desc"|"created_asc"|"updated_desc"|"last_active_desc"|"name_asc"|"name_desc"} mode
 */
export function compareContacts(a, b, mode) {
  const idDesc = () => String(b.id).localeCompare(String(a.id));
  const idAsc = () => String(a.id).localeCompare(String(b.id));
  switch (mode) {
    case "created_desc": {
      const pa = contactEffectiveCreatedMs(a);
      const pb = contactEffectiveCreatedMs(b);
      if (pb !== pa) return pb - pa;
      return idDesc();
    }
    case "created_asc": {
      const pa = contactEffectiveCreatedMs(a);
      const pb = contactEffectiveCreatedMs(b);
      if (pa !== pb) return pa - pb;
      return idAsc();
    }
    case "updated_desc": {
      const pa = contactUpdatedAtMs(a) || contactEffectiveCreatedMs(a);
      const pb = contactUpdatedAtMs(b) || contactEffectiveCreatedMs(b);
      if (pb !== pa) return pb - pa;
      return idDesc();
    }
    case "last_active_desc": {
      const pa = contactLastActiveMs(a);
      const pb = contactLastActiveMs(b);
      if (pb !== pa) return pb - pa;
      return idDesc();
    }
    case "name_asc": {
      const n = String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
      return n || idAsc();
    }
    case "name_desc": {
      const n = String(b.name || "").localeCompare(String(a.name || ""), undefined, { sensitivity: "base" });
      return n || idDesc();
    }
    default:
      return idDesc();
  }
}

function mapDocsSortByCreatedAtDesc(docs) {
  const rows = docs.map((d) => normalizeContactForCRM({ id: d.id, ...d.data() }));
  rows.sort((a, b) => compareContacts(a, b, "created_desc"));
  return rows;
}

// ── Contacts ─────────────────────────────────

export function watchContacts(callback, onError) {
  const q = query(collection(db, CONTACTS));
  return onSnapshot(
    q,
    snap => callback(mapDocsSortByCreatedAtDesc(snap.docs)),
    err => {
      console.error(err);
      onError?.(err);
    }
  );
}

export async function getContact(id) {
  const snap = await getDoc(doc(db, CONTACTS, id));
  return snap.exists() ? normalizeContactForCRM({ id: snap.id, ...snap.data() }) : null;
}

/** Live updates for a single profile (e.g. contact panel after edits or stage changes). */
export function watchContact(id, callback, onError) {
  if (!id) return () => {};
  return onSnapshot(
    doc(db, CONTACTS, id),
    (snap) => {
      if (!snap.exists()) callback(null);
      else callback(normalizeContactForCRM({ id: snap.id, ...snap.data() }));
    },
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export async function createContact(data) {
  const tags = computeAutoTags(data, data.tags || []);
  const contact = {
    ...data,
    tags,
    stage: data.stage || "lead",
    isInternal: data.isInternal === true,
    healthScoreManual: data.healthScoreManual === true,
    healthScore: data.healthScoreManual === true && typeof data.healthScore === "number" ? data.healthScore : deriveContactHealthScore(data),
    leadScoreManual: data.leadScoreManual === true,
    leadScore: data.leadScoreManual === true && typeof data.leadScore === "number" ? data.leadScore : deriveContactLeadScore(data),
    mrr: data.mrr ?? 0,
    totalScans: data.totalScans ?? 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, CONTACTS), contact);
  return ref.id;
}

export async function updateContact(id, data) {
  const existing = await getContact(id);
  const merged = { ...existing, ...data };
  const tags = computeAutoTags(merged, data.tags ?? existing?.tags ?? []);
  await updateDoc(doc(db, CONTACTS, id), {
    ...data,
    tags,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteContact(id) {
  await deleteDoc(doc(db, CONTACTS, id));
}

// ── Contact merge ─────────────────────────────
export async function mergeContacts(primaryId, duplicateId) {
  const [primary, duplicate] = await Promise.all([
    getContact(primaryId), getContact(duplicateId)
  ]);
  if (!primary || !duplicate) throw new Error("Contact not found");

  const batch = writeBatch(db);

  // Merge fields — primary wins, but fill blanks from duplicate
  const merged = {
    ...duplicate,
    ...primary,
    tags: [...new Set([...(primary.tags || []), ...(duplicate.tags || [])])],
    isInternal: primary.isInternal === true || duplicate.isInternal === true,
    healthScoreManual: primary.healthScoreManual === true,
    leadScoreManual: primary.leadScoreManual === true,
    mrr: Math.max(primary.mrr || 0, duplicate.mrr || 0),
    totalScans: (primary.totalScans || 0) + (duplicate.totalScans || 0),
    mergedFrom: arrayUnion(duplicateId),
    updatedAt: serverTimestamp(),
  };
  merged.healthScore = merged.healthScoreManual ? primary.healthScore : deriveContactHealthScore(merged);
  merged.leadScore = merged.leadScoreManual ? primary.leadScore : deriveContactLeadScore(merged);
  batch.update(doc(db, CONTACTS, primaryId), merged);
  batch.delete(doc(db, CONTACTS, duplicateId));

  // Log merge activity
  batch.set(doc(collection(db, CONTACTS, primaryId, "activity")), {
    type: "contact_merged",
    text: `Merged with ${duplicate.name} (${duplicate.email})`,
    contactId: primaryId,
    createdAt: serverTimestamp(),
  });

  await batch.commit();
  return primaryId;
}

// ── Companies ────────────────────────────────

export function watchCompanies(callback, onError) {
  const q = query(collection(db, COMPANIES));
  return onSnapshot(
    q,
    snap => callback(mapDocsSortByCreatedAtDesc(snap.docs)),
    err => {
      console.error(err);
      onError?.(err);
    }
  );
}

export async function createCompany(data) {
  const ref = await addDoc(collection(db, COMPANIES), {
    ...data,
    contactIds: data.contactIds || [],
    totalMrr: data.totalMrr || 0,
    totalScans: data.totalScans || 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateCompany(id, data) {
  await updateDoc(doc(db, COMPANIES, id), { ...data, updatedAt: serverTimestamp() });
}

// ── Activity timeline ────────────────────────

function activityTimeMs(row) {
  const v = row.createdAt ?? row.updatedAt ?? row.timestamp ?? row.created_at ?? row.updated_at;
  if (v == null) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Subcollection timeline — tries several order fields, then unordered limit + client sort. */
export function watchActivity(contactId, callback, onError) {
  const collRef = collection(db, CONTACTS, contactId, "activity");
  const mapDoc = (d) => ({ id: d.id, ...d.data() });
  const orderAttempts = ["created_at", "updated_at", "createdAt", "updatedAt", "timestamp"];
  let attempt = 0;
  let unsub = null;
  const stop = () => {
    if (unsub) {
      unsub();
      unsub = null;
    }
  };

  const runLimitFallback = () => {
    stop();
    unsub = onSnapshot(
      query(collRef, limit(80)),
      (snap) => {
        const rows = snap.docs.map(mapDoc);
        rows.sort((a, b) => activityTimeMs(b) - activityTimeMs(a));
        callback(rows.slice(0, 50));
      },
      (err) => {
        console.error("[watchActivity] fallback failed", err);
        onError?.(err);
        callback([]);
      }
    );
  };

  const tryOrder = () => {
    stop();
    if (attempt >= orderAttempts.length) {
      runLimitFallback();
      return;
    }
    const field = orderAttempts[attempt++];
    unsub = onSnapshot(
      query(collRef, orderBy(field, "desc"), limit(50)),
      (snap) => callback(snap.docs.map(mapDoc)),
      (err) => {
        console.warn(`[watchActivity] orderBy(${field}) failed — trying next`, err?.code || err?.message);
        tryOrder();
      }
    );
  };

  tryOrder();
  return stop;
}

/** Same listener pattern as `activity`, for `email_activity` (SendGrid / sequences) or other subcollections. */
export function watchSubtimeline(contactId, subcollection, callback, onError) {
  if (!contactId || !subcollection) return () => {};
  const collRef = collection(db, CONTACTS, contactId, subcollection);
  const mapDoc = (d) => ({ id: `${subcollection}_${d.id}`, ...d.data() });
  const orderAttempts = ["created_at", "updated_at", "createdAt", "updatedAt", "timestamp"];
  let attempt = 0;
  let unsub = null;
  const stop = () => {
    if (unsub) {
      unsub();
      unsub = null;
    }
  };
  const runLimitFallback = () => {
    stop();
    unsub = onSnapshot(
      query(collRef, limit(80)),
      (snap) => {
        const rows = snap.docs.map(mapDoc);
        rows.sort((a, b) => activityTimeMs(b) - activityTimeMs(a));
        callback(rows.slice(0, 50));
      },
      (err) => {
        console.error("[watchSubtimeline] fallback failed", subcollection, err);
        onError?.(err);
        callback([]);
      }
    );
  };
  const tryOrder = () => {
    stop();
    if (attempt >= orderAttempts.length) {
      runLimitFallback();
      return;
    }
    const field = orderAttempts[attempt++];
    unsub = onSnapshot(
      query(collRef, orderBy(field, "desc"), limit(50)),
      (snap) => callback(snap.docs.map(mapDoc)),
      (err) => {
        console.warn(`[watchSubtimeline:${subcollection}] orderBy(${field}) failed`, err?.code || err?.message);
        tryOrder();
      }
    );
  };
  tryOrder();
  return stop;
}

export async function logActivity(contactId, type, text, metadata = {}) {
  await addDoc(collection(db, CONTACTS, contactId, "activity"), {
    type, text, contactId, metadata,
    createdAt: serverTimestamp(),
  });
  // Also write to global feed for dashboard
  await addDoc(collection(db, "activity_feed"), {
    type, text, contactId, metadata,
    createdAt: serverTimestamp(),
  });
}

/** Email / sequence rows for the contact timeline (written by webhooks or CRM). */
export async function logEmailActivity(contactId, type, text, metadata = {}) {
  await addDoc(collection(db, CONTACTS, contactId, "email_activity"), {
    type,
    text,
    contactId,
    metadata,
    createdAt: serverTimestamp(),
  });
}

// ── Deals (enterprise / forecasting) ─────────

const DEALS = "deals";

export function watchDeals(callback, onError) {
  return onSnapshot(
    collection(db, DEALS),
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => createdAtMs(b) - createdAtMs(a));
      callback(rows);
    },
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export function watchDeal(dealId, callback, onError) {
  if (!dealId) return () => {};
  return onSnapshot(
    doc(db, DEALS, dealId),
    (snap) => {
      if (!snap.exists()) callback(null);
      else callback({ id: snap.id, ...snap.data() });
    },
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export async function createDeal(data) {
  const ref = await addDoc(collection(db, DEALS), {
    title: data.title || "Untitled deal",
    value: Number(data.value) || 0,
    currency: data.currency || "usd",
    stage: data.stage || "lead",
    contactId: data.contactId || null,
    contactName: data.contactName || "",
    companyName: String(data.companyName ?? "").trim(),
    pocName: String(data.pocName ?? "").trim(),
    website: String(data.website ?? "").trim(),
    notes: data.notes || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateDeal(id, data) {
  await updateDoc(doc(db, DEALS, id), { ...data, updatedAt: serverTimestamp() });
}

export async function logDealActivity(dealId, type, text, metadata = {}) {
  const actRef = doc(collection(db, DEALS, dealId, "activity"));
  const batch = writeBatch(db);
  batch.set(actRef, {
    type,
    text,
    dealId,
    metadata,
    createdAt: serverTimestamp(),
  });
  batch.update(doc(db, DEALS, dealId), { updatedAt: serverTimestamp() });
  await batch.commit();
}

export function watchDealActivity(dealId, callback, onError) {
  if (!dealId) return () => {};
  const collRef = collection(db, DEALS, dealId, "activity");
  const mapDoc = (d) => ({ id: `deal_${d.id}`, ...d.data() });
  const orderAttempts = ["created_at", "updated_at", "createdAt", "updatedAt", "timestamp"];
  let attempt = 0;
  let unsub = null;
  const stop = () => {
    if (unsub) {
      unsub();
      unsub = null;
    }
  };
  const runLimitFallback = () => {
    stop();
    unsub = onSnapshot(
      query(collRef, limit(80)),
      (snap) => {
        const rows = snap.docs.map(mapDoc);
        rows.sort((a, b) => activityTimeMs(b) - activityTimeMs(a));
        callback(rows.slice(0, 50));
      },
      (err) => {
        console.error("[watchDealActivity] fallback failed", err);
        onError?.(err);
        callback([]);
      }
    );
  };
  const tryOrder = () => {
    stop();
    if (attempt >= orderAttempts.length) {
      runLimitFallback();
      return;
    }
    const field = orderAttempts[attempt++];
    unsub = onSnapshot(
      query(collRef, orderBy(field, "desc"), limit(50)),
      (snap) => callback(snap.docs.map(mapDoc)),
      (err) => {
        console.warn(`[watchDealActivity] orderBy(${field}) failed`, err?.code || err?.message);
        tryOrder();
      }
    );
  };
  tryOrder();
  return stop;
}

// ── Support tickets ────────────────────────────

const TICKETS = "support_tickets";

export function watchSupportTickets(callback, onError) {
  return onSnapshot(
    collection(db, TICKETS),
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => createdAtMs(b) - createdAtMs(a));
      callback(rows);
    },
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export async function createSupportTicket(data) {
  const ref = await addDoc(collection(db, TICKETS), {
    subject: data.subject || "Support request",
    status: "open",
    priority: data.priority || "normal",
    contactId: data.contactId || null,
    contactEmail: data.contactEmail || "",
    source: data.source || "manual",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateSupportTicket(id, data) {
  await updateDoc(doc(db, TICKETS, id), { ...data, updatedAt: serverTimestamp() });
}

export function watchTicketMessages(ticketId, callback, onError) {
  if (!ticketId) return () => {};
  const collRef = collection(db, TICKETS, ticketId, "messages");
  return onSnapshot(
    query(collRef, limit(100)),
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => activityTimeMs(a) - activityTimeMs(b));
      callback(rows);
    },
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export async function addTicketMessage(ticketId, text, author = "Agent", internal = false) {
  await addDoc(collection(db, TICKETS, ticketId, "messages"), {
    text,
    author,
    internal,
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, TICKETS, ticketId), { updatedAt: serverTimestamp() });
}

export function watchGlobalActivity(callback, onError) {
  const collRef = collection(db, "activity_feed");
  const mapDoc = (d) => ({ id: d.id, ...d.data() });
  const orderAttempts = ["created_at", "updated_at", "createdAt", "updatedAt", "timestamp"];
  let attempt = 0;
  let unsub = null;
  const stop = () => {
    if (unsub) {
      unsub();
      unsub = null;
    }
  };

  const runLimitFallback = () => {
    stop();
    unsub = onSnapshot(
      query(collRef, limit(60)),
      (snap) => {
        const rows = snap.docs.map(mapDoc);
        rows.sort((a, b) => activityTimeMs(b) - activityTimeMs(a));
        callback(rows.slice(0, 30));
      },
      (err) => {
        console.error("[watchGlobalActivity] fallback failed", err);
        onError?.(err);
        callback([]);
      }
    );
  };

  const tryOrder = () => {
    stop();
    if (attempt >= orderAttempts.length) {
      runLimitFallback();
      return;
    }
    const field = orderAttempts[attempt++];
    unsub = onSnapshot(
      query(collRef, orderBy(field, "desc"), limit(30)),
      (snap) => callback(snap.docs.map(mapDoc)),
      (err) => {
        console.warn(`[watchGlobalActivity] orderBy(${field}) failed — trying next`, err?.code || err?.message);
        tryOrder();
      }
    );
  };

  tryOrder();
  return stop;
}

// ── Tasks ────────────────────────────────────

/** All tasks (no server-side order) — sort in the UI. Supports optional equality filters. */
export function watchTasks(callback, onError, filters = {}) {
  const coll = collection(db, "tasks");
  const constraints = [];
  if (filters.assignedTo) constraints.push(where("assignedTo", "==", filters.assignedTo));
  if (filters.status) constraints.push(where("status", "==", filters.status));
  const q = constraints.length ? query(coll, ...constraints) : query(coll);
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err => {
      console.error(err);
      onError?.(err);
    }
  );
}

export function watchTasksForContact(contactId, callback, onError) {
  if (!contactId) return () => {};
  const q = query(collection(db, "tasks"), where("contactId", "==", contactId));
  return onSnapshot(
    q,
    snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const am = a.dueAt?.toMillis?.() ?? (a.dueAt instanceof Date ? a.dueAt.getTime() : 0);
        const bm = b.dueAt?.toMillis?.() ?? (b.dueAt instanceof Date ? b.dueAt.getTime() : 0);
        return am - bm;
      });
      callback(list);
    },
    err => {
      console.error(err);
      onError?.(err);
    }
  );
}

export function watchTasksForDeal(dealId, callback, onError) {
  if (!dealId) return () => {};
  const q = query(collection(db, "tasks"), where("dealId", "==", dealId));
  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const am = a.dueAt?.toMillis?.() ?? (a.dueAt instanceof Date ? a.dueAt.getTime() : 0);
        const bm = b.dueAt?.toMillis?.() ?? (b.dueAt instanceof Date ? b.dueAt.getTime() : 0);
        return am - bm;
      });
      callback(list);
    },
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export async function createTask(data) {
  const ref = await addDoc(collection(db, "tasks"), {
    ...data,
    status: "open",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  if (data.contactId) {
    await logActivity(data.contactId, "task_created", `Task created: ${data.title}`);
  }
  if (data.dealId) {
    await logDealActivity(data.dealId, "task_created", `Task created: ${data.title}`, { taskId: ref.id });
  }
  return ref.id;
}

/**
 * Idempotent task creator. Skips if any task already exists with this `autoKey`,
 * so reactive triggers (payment fail, churn risk, upsell ready) can run on every
 * snapshot without duplicating work. Use a stable key like
 *   `pay-fail:<contactId>:<YYYY-WW>` or `upsell:<contactId>:<YYYY-MM>`.
 */
export async function ensureAutoTask(autoKey, data) {
  if (!autoKey) throw new Error("ensureAutoTask requires autoKey");
  const existing = await getDocs(
    query(collection(db, "tasks"), where("autoKey", "==", autoKey), limit(1))
  );
  if (!existing.empty) return existing.docs[0].id;
  return createTask({ ...data, autoKey, source: data.source || "auto" });
}

export async function updateTask(id, data) {
  const prevSnap = await getDoc(doc(db, "tasks", id));
  const prev = prevSnap.data() || {};
  await updateDoc(doc(db, "tasks", id), { ...data, updatedAt: serverTimestamp() });
  const contactId = data.contactId ?? prev.contactId;
  const dealId = data.dealId ?? prev.dealId;
  if (data.status === "completed") {
    const task = await getDoc(doc(db, "tasks", id));
    const title = task.data()?.title;
    if (contactId) await logActivity(contactId, "task_completed", `Task completed: ${title}`);
    if (dealId) await logDealActivity(dealId, "task_completed", `Task completed: ${title}`, { taskId: id });
  }
}

// ── Stage transition ─────────────────────────

export async function transitionStage(contactId, newStage, triggeredBy = "manual") {
  const contact = await getContact(contactId);
  if (!contact) throw new Error("Contact not found");
  const oldStage = contact.stage;

  const updates = {
    stage: newStage,
    [`stageHistory.${newStage}`]: serverTimestamp(),
  };
  if (newStage === "paid_customer") {
    updates.convertedAt = serverTimestamp();
  }

  await updateContact(contactId, updates);
  await logActivity(contactId, "stage_changed",
    `Stage: ${oldStage} → ${newStage}`,
    { oldStage, newStage, triggeredBy }
  );
}

// ── Duplicate detection ──────────────────────

export async function findDuplicates() {
  const snap = await getDocs(collection(db, CONTACTS));
  const contacts = snap.docs.map(d => normalizeContactForCRM({ id: d.id, ...d.data() }));
  const seen = {};
  const dupes = [];

  contacts.forEach(c => {
    const key = c.email?.toLowerCase();
    if (!key) return;
    if (seen[key]) {
      dupes.push({ a: seen[key], b: c });
    } else {
      seen[key] = c;
    }
  });
  return dupes;
}

// ── Auto-tag engine ──────────────────────────

function computeAutoTags(contact, existingTags) {
  const manual = (existingTags || []).filter(t =>
    !AUTO_TAG_RULES.map(r => r.tag).includes(t)
  );
  const auto = AUTO_TAG_RULES
    .filter(r => { try { return r.condition(contact); } catch { return false; } })
    .map(r => r.tag);
  return [...new Set([...manual, ...auto])];
}

// ── CSV helpers ──────────────────────────────

export function contactsToCSV(contacts) {
  const headers = ["Name","Email","Company","Industry","Stage","Plan","Invoiced","Internal","Health Score","Tags","Created At"];
  const rows = contacts.map(c => [
    c.name, c.email, c.companyName, c.industry || "", c.stage, c.plan,
    c.lifetimeRevenue ?? c.mrr ?? "", c.isInternal ? "yes" : "",
    c.healthScore,
    (c.tags || []).join(";"),
    c.createdAt?.toDate?.()?.toISOString?.() || c.createdAt || ""
  ]);
  return [headers, ...rows].map(r => r.map(v => `"${v ?? ""}"`).join(",")).join("\n");
}

// ── Product collections (analytics / revenue) ─────────────────

export function watchPaymentsCollection(callback, onError) {
  return onSnapshot(
    collection(db, "payments"),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export function watchOrdersCollection(callback, onError) {
  return onSnapshot(
    collection(db, "orders"),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export function watchScansCollection(callback, onError) {
  return onSnapshot(
    collection(db, "scans"),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export function watchFilesCollection(callback, onError) {
  return onSnapshot(
    collection(db, "files"),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export function watchWorkspacesCollection(callback, onError) {
  return onSnapshot(
    collection(db, "workspaces"),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export function watchWorkspaceMembersCollection(callback, onError) {
  return onSnapshot(
    collection(db, "workspace_members"),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export function watchProductAnalyticsEventsCollection(callback, onError) {
  return onSnapshot(
    collection(db, "product_analytics_events"),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error(err);
      onError?.(err);
    }
  );
}

export function parseCSVToContacts(csv) {
  const lines = csv.trim().split("\n");
  const headers = lines[0].split(",").map(h => h.replace(/"/g, "").trim().toLowerCase());
  return lines.slice(1).map(line => {
    const vals = line.split(",").map(v => v.replace(/"/g, "").trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ""; });
    return {
      name:        obj.name || obj["full name"] || "",
      email:       obj.email || "",
      companyName: obj.company || obj["company name"] || "",
      stage:       obj.stage || "lead",
      plan:        obj.plan || "Free",
      mrr:         parseFloat(obj.mrr) || 0,
      tags:        obj.tags ? obj.tags.split(";").filter(Boolean) : [],
    };
  }).filter(c => c.name || c.email);
}

export { serverTimestamp, Timestamp };
